import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import Checkmark from '../../../SvgIcons/Checkmark';
import Remove from '../../../SvgIcons/Remove';
import { Error } from '../../Error';
import FormTooltip from '../../common/FormTooltip';
import InputFieldAddOn from '../InputFieldAddOn';
import type { InputFieldAddOnType } from '../InputFieldAddOn';
import Eye from '../../../SvgIcons/Eye';
import HiddenEye from '../../../SvgIcons/HiddenEye';
import { renderLabelLink, calculateNotificationStyle } from '../../util/renderHelpers';
import '../../formV2.less';

interface LabelLinkProps {
  url: string;
  copy: string;
}

export enum Alignment {
  vertical = 'vertical',
  horizontal = 'horizontal',
}

export interface InputFieldV2Props extends React.HTMLProps<HTMLInputElement> {
  className?: string;
  id?: string;
  value?: string;
  onChange?: React.FormEventHandler<HTMLInputElement>;
  defaultValue?: string;
  label?: string;
  labelLink?: LabelLinkProps | React.ReactNode;
  placeholder?: string;
  /** for showing red asterisk on label */
  isRequired?: boolean;
  /** shows helper text below input field */
  helperText?: string | JSX.Element;
  /** shows tooltip on hover of info icon */
  tooltip?: string;
  /** pass classname to adjust tooltip width according to text */
  tooltipClassname?: string;
  /** adds 'x' to input field and allows clearing of value */
  clear?: boolean;
  onClear?: () => void;
  /** error message shown below input field; passed in by rff or redux form validation */
  errorMessage?: React.ReactNode;
  /** success message; needs to be passed in explicitly */
  successMessage?: React.ReactNode;
  /** *deprecated* when true, displays 'That one is available' success message */
  isAvailable?: boolean;
  /** display input length count, cycles through warning and error states based on increasing input length relative to maxChars. NOTE: you will still need to add a max validation function. this prop is only responsible for the count display */
  maxChars?: number;
  /** adds icon to the beginning of the field  */
  addOn?: InputFieldAddOnType;
  /** applies inline-block to form fields */
  inline?: boolean;
  /** @deprecated */
  allowChangeToEmpty?: boolean;
  /** alignment of label and input field */
  alignment?: Alignment;
  /** icon is either Eye or HiddenEye icon for password visibility  */
  password?: boolean;
  onTogglePasswordVisibility?: () => void;
}

/**
 * ```import { InputFieldV2 } from '@zola/zola-ui/src/components/InputFieldV2';```
 *
 * Base input field component
 *
 *    - wrapper for `<input />` tag that implements error messaging, helper text, helper links, etc.
 *    - base for `<InputFieldV2Adapter />` and `<FormField />`
 */
const InputFieldV2: React.FC<InputFieldV2Props> = ({
  className,
  type,
  id,
  value: externalValue = '',
  onChange,
  defaultValue,
  label,
  labelLink,
  placeholder,
  isRequired,
  helperText,
  tooltip,
  tooltipClassname,
  clear,
  onClear,
  errorMessage,
  successMessage: successMessageProp,
  isAvailable,
  maxChars,
  addOn,
  inline,
  allowChangeToEmpty,
  alignment = Alignment.vertical,
  children,
  password,
  onTogglePasswordVisibility,
  ...rest
}) => {
  const phoneNumber = type === 'tel';
  const successMessage = isAvailable ? 'That one is available' : successMessageProp;

  const initialInputValue = externalValue || defaultValue || '';

  const [inputValue, setInputValue] = useState(initialInputValue);
  const [eyeIcon, setEyeIcon] = useState(<HiddenEye />);

  const EyeIconWrapper = (): JSX.Element => (
    <span
      onClick={onTogglePasswordVisibility}
      data-testid={type === 'password' ? 'hidden-eye' : 'eye'}
      className="eyeball-icon__container"
    >
      {eyeIcon}
    </span>
  );

  const handleClear = () => {
    if (onClear && typeof onClear === 'function') {
      onClear();
    } else {
      setInputValue('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    setInputValue(e.target.value);
  };

  const maxInvalid =
    maxChars && calculateNotificationStyle('max_invalid', maxChars, inputValue.length);
  const maxWarning =
    maxChars && calculateNotificationStyle('warning', maxChars, inputValue.length, 5);
  const showSuccessState = isAvailable;
  const showErrorState = errorMessage || maxInvalid;
  const showWarningState = maxWarning && !showErrorState;
  const inputContainerClasses = classNames(
    'zola-ui v2-render-field',
    { inline },
    { 'horiz-align': alignment === Alignment.horizontal },
    { 'has-add-on': addOn },
    {
      'has-warning': showWarningState,
    },
    {
      'has-success': showSuccessState,
    },
    {
      'has-error': showErrorState,
    },
    className
  );

  useEffect(() => {
    if (allowChangeToEmpty || (externalValue && externalValue !== inputValue)) {
      setInputValue(externalValue);
    }
  }, [externalValue, allowChangeToEmpty, inputValue, setInputValue]);

  useEffect(() => {
    if (password) {
      if (type === 'text') {
        setEyeIcon(<Eye />);
      } else {
        setEyeIcon(<HiddenEye />);
      }
    }
  }, [password, type]);

  return (
    <div className={inputContainerClasses}>
      {label && (
        <label htmlFor={id}>
          <span className="label__text">{label}</span>
          {isRequired && <span className="text-danger danger-asterisk">*</span>}
          {tooltip && <FormTooltip text={tooltip} className={tooltipClassname} />}
          {renderLabelLink('desktop', labelLink)}
        </label>
      )}
      <div className="input-field__container">
        <InputFieldAddOn type={addOn} />
        <input
          {...rest}
          value={inputValue}
          className="input-field"
          type={type}
          id={id}
          placeholder={phoneNumber ? '(         )   ___-____' : placeholder}
          onChange={handleChange}
        />
        {children}
        {password && <EyeIconWrapper />}
        {clear && inputValue.length !== 0 && (
          <span className="remove-icon__container">
            <Remove
              className="form-field__icon remove"
              width={12}
              height={12}
              title="remove-icon"
              onClick={() => handleClear()}
            />
          </span>
        )}
        <div className="field-message-container">
          {maxChars && !errorMessage && (
            <span className="field-message">
              <span
                className={classNames(
                  'max-characters',
                  { 'text-danger': maxInvalid },
                  {
                    'text-warning': maxWarning,
                  }
                )}
              >
                {inputValue.length}/{maxChars}
              </span>
            </span>
          )}
          {renderLabelLink('mobile', labelLink)}
          {helperText && !errorMessage && <div className="field-message">{helperText}</div>}
          {errorMessage && <Error message={errorMessage} iconSize={12} />}
          {successMessage && (
            <span className="field-message">
              <Checkmark
                className="form-field__icon success"
                width={12}
                height={12}
                title="checkmark-icon"
              />
              <span className="text-success">{successMessage}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InputFieldV2;
